
import { useState } from 'react';
import Cookies from 'js-cookie'
import axios from 'axios'
import { useNavigate } from 'react-router-dom';

const Hero = () => {
  const navigate = useNavigate();

  const [firstName, setFirstName] = useState("")
  const [lastName, setLastName] = useState("")
  const [email, setEmail] = useState("")
  const [country, setCountry] = useState("United States of America")

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleSubmitForm = async (e: React.FormEvent) => {
    e.preventDefault();

    const sponsor = Cookies.get('sponsor')

    if (!sponsor) {
      setError('A referral is required. Please use a referral link (e.g., go20x.com/username)')
      return
    }

    if (!firstName || !lastName || !email || !country) {
      setError('All fields are required!')
      return
    }

    setError("")
    setIsSubmitting(true)

    try {
      const res = await axios.post(`${import.meta.env.VITE_BASE_API}/contact/landing-page`, {
        name: firstName,
        lastName: lastName,
        email: email,
        country: country,
        sponsorId: sponsor || null
      })

      if (res?.data?.ok) {
        // Success - redirect to shopxcelerate.com with referral username
        window.location.href = `https://shopxcelerate.com/${sponsor}`
      } else {
        setError(res?.data?.msg || 'Something went wrong. Please try again.')
        setIsSubmitting(false)
      }

    } catch (err: any) {
      console.error('Form submission error:', err)
      setError(err?.response?.data?.msg || 'Something went wrong. Please try again.')
      setIsSubmitting(false)
    }
  }

  return (
    <>
      <style>{`
        .ml-form-embedSubmitLoad {
          display: inline-block;
          width: 20px;
          height: 20px;
        }
        .ml-form-embedSubmitLoad:after {
          content: " ";
          display: block;
          width: 11px;
          height: 11px;
          margin: 1px;
          border-radius: 50%;
          border: 4px solid #fff;
          border-color: #ffffff #ffffff #ffffff transparent;
          animation: ml-form-embedSubmitLoad 1.2s linear infinite;
        }
        @keyframes ml-form-embedSubmitLoad {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }
      `}</style>

      <section
        className="relative min-h-screen text-white overflow-hidden"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.8), rgba(0, 0, 0, 0.8)), url('https://readdy.ai/api/search-image?query=stunning%20travel%20destinations%20collage%20with%20mountains%2C%20beaches%2C%20ancient%20temples%2C%20modern%20cities%2C%20adventure%20activities%2C%20world%20landmarks%2C%20beautiful%20landscapes%2C%20inspiring%20wanderlust%20atmosphere%2C%20professional%20travel%20photography%2C%20cinematic%20composition&width=1920&height=1080&seq=hero-travel-bg&orientation=landscape')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      >
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-32 h-32 border border-orange-400 rotate-45"></div>
          <div className="absolute top-40 right-32 w-24 h-24 border border-orange-400 rotate-12"></div>
          <div className="absolute bottom-32 left-1/4 w-16 h-16 border border-orange-400 rotate-45"></div>
        </div>

        <div className="relative z-10 container mx-auto px-6 py-20">
          {/* Top Text Section - Wider Spread */}
          <div className="text-center mb-20 max-w-7xl mx-auto">
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-8 leading-tight">
              <span className="text-orange-400">Get Paid to Live the Life</span><br />
              <span className="text-white">Everyone Else </span>
              <span className="text-orange-400">Posts About</span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
              Your membership unlocks private wholesale pricing on 3 million hotels, resorts, and tours worldwide — plus weekly affiliate commission paid every Friday. This isn't just a discount club; it's a gateway to freedom, luxury, and lifestyle income.
            </p>
          </div>

          {/* Main Content Section - Equal Heights */}
          <div className="grid lg:grid-cols-5 gap-12 items-stretch max-w-7xl mx-auto">
            {/* Left Side - Video */}
            <div className="lg:col-span-3 w-full">
              <div className="relative w-full h-full">
                <div style={{ padding: '56.25% 0 0 0', position: 'relative', width: '100%' }}>
                  <iframe
                    src="https://player.vimeo.com/video/1131127859?badge=0&autopause=0&player_id=0&app_id=58479"
                    frameBorder="0"
                    allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
                    referrerPolicy="strict-origin-when-cross-origin"
                    style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                    title="New Savings Club"
                    className="rounded-2xl shadow-2xl"
                  ></iframe>
                </div>
              </div>
            </div>

            {/* Right Side - Form */}
            <div id="hero-form" className="lg:col-span-2 bg-gray-800/50 backdrop-blur-sm rounded-2xl p-6 shadow-2xl border border-gray-700 flex flex-col justify-between">
              <div className="mb-3">
                <h2 className="text-2xl font-bold text-orange-400 mb-2">Show Me More</h2>
                <div className="w-16 h-1 bg-orange-400"></div>
              </div>

              {error && (
                <div className="mb-4 p-3 bg-red-500/20 border border-red-500/50 rounded-lg text-red-300 text-sm">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmitForm} className="space-y-3" data-readdy-form>
                <div className="space-y-3">
                  <div>
                    <input
                      type="text"
                      name="name"
                      placeholder="First Name"
                      value={firstName}
                      onChange={e => setFirstName(e.target.value)}
                      required
                      disabled={isSubmitting}
                      className="w-full px-3 py-2 bg-gray-700/50 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-orange-400 focus:ring-1 focus:ring-orange-400 transition-colors disabled:opacity-50"
                    />
                  </div>

                  <div>
                    <input
                      type="text"
                      name="lastName"
                      placeholder="Last Name"
                      value={lastName}
                      onChange={e => setLastName(e.target.value)}
                      required
                      disabled={isSubmitting}
                      className="w-full px-3 py-2 bg-gray-700/50 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-orange-400 focus:ring-1 focus:ring-orange-400 transition-colors disabled:opacity-50"
                    />
                  </div>

                  <div>
                    <input
                      type="email"
                      name="email"
                      placeholder="Email Address"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      required
                      disabled={isSubmitting}
                      className="w-full px-3 py-2 bg-gray-700/50 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-orange-400 focus:ring-1 focus:ring-orange-400 transition-colors disabled:opacity-50"
                    />
                  </div>

                  <div className="relative">
                    <select
                      name="country"
                      value={country}
                      onChange={e => setCountry(e.target.value)}
                      required
                      disabled={isSubmitting}
                      className="w-full px-3 py-2 pr-8 bg-gray-700/50 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-orange-400 focus:ring-1 focus:ring-orange-400 transition-colors appearance-none cursor-pointer disabled:opacity-50"
                    >
                      <option value="">Select Country</option>
                      <option value="United States of America">United States of America</option>
                      <option value="Canada">Canada</option>
                      <option value="United Kingdom">United Kingdom</option>
                      <option value="Australia">Australia</option>
                      <option value="Germany">Germany</option>
                      <option value="France">France</option>
                      <option value="Spain">Spain</option>
                      <option value="Italy">Italy</option>
                      <option value="Mexico">Mexico</option>
                      <option value="Brazil">Brazil</option>
                      <option value="India">India</option>
                      <option value="Philippines">Philippines</option>
                      <option value="Nigeria">Nigeria</option>
                      <option value="South Africa">South Africa</option>
                      <option value="Other">Other</option>
                    </select>
                    <div className="absolute right-4 top-1/2 transform -translate-y-1/2 pointer-events-none">
                      <i className="ri-arrow-down-s-line text-gray-400"></i>
                    </div>
                  </div>
                </div>

                <div className="mt-4 space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => {
                        const sponsor = Cookies.get('sponsor')
                        if (!sponsor) {
                          setError('A referral is required. Please use a referral link (e.g., go20x.com/username)')
                          return
                        }
                        if (!firstName || !lastName || !email || !country) {
                          setError('Please fill out all fields first!')
                          return
                        }
                        setError('')
                        window.location.href = `https://shopxcelerate.com/enroll?ref=${sponsor}&first_name=${encodeURIComponent(firstName)}&last_name=${encodeURIComponent(lastName)}&email=${encodeURIComponent(email)}&country=${encodeURIComponent(country)}`
                      }}
                      className="w-full bg-green-500 hover:bg-green-600 text-white font-bold py-3 px-4 rounded-lg transition-colors whitespace-nowrap cursor-pointer text-center"
                    >
                      Join Now
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-orange-500 hover:bg-orange-600 text-white font-bold py-3 px-4 rounded-lg transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? (
                        <>
                          <span className="ml-form-embedSubmitLoad inline-block mr-2"></span>
                          Sending...
                        </>
                      ) : (
                        'Email Info'
                      )}
                    </button>
                  </div>

                  <p className="text-white text-xs text-center opacity-60">
                    Join now to get started, or get more info via email.
                  </p>
                </div>

              </form>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Hero;
